import { Injectable } from '@angular/core';

/**
 * The single owner of `document.title`.
 *
 * Two independent things want to write the tab title:
 *   - customer branding — the tab name set in the Branding editor, resolved either from the signed-in
 *     user's profile or, on the login screen, from the portal domain the browser is on;
 *   - the unread-message badge in the layout toolbar — "📬 (3) …".
 *
 * They used to write `document.title` directly, and the badge hardcoded 'Sypore Portal', so any
 * branded title was silently reverted the moment a message arrived. Both now report their piece here
 * and this service composes the result, so neither can clobber the other regardless of ordering.
 */
@Injectable({ providedIn: 'root' })
export class PortalTitleService {
  /** Shown when a customer hasn't set a tab name. Matches the <title> in index.html. */
  static readonly DEFAULT_TITLE = 'Sypore Portal';

  private brandTitle: string | null = null;
  private unreadCount = 0;

  /** The customer's tab name, or null to fall back to the app default. */
  setBrandTitle(title: string | null | undefined): void {
    const next = title?.trim() || null;
    if (next === this.brandTitle) return;
    this.brandTitle = next;
    this.apply();
  }

  setUnreadCount(count: number): void {
    const next = count > 0 ? count : 0;
    if (next === this.unreadCount) return;
    this.unreadCount = next;
    this.apply();
  }

  /** The tab name without the unread badge — for anywhere that needs the brand name as text. */
  get baseTitle(): string {
    return this.brandTitle ?? PortalTitleService.DEFAULT_TITLE;
  }

  private apply(): void {
    const mailboxEmoji = String.fromCodePoint(0x1f4ec);
    document.title = this.unreadCount > 0
      ? `${mailboxEmoji} (${this.unreadCount}) ${this.baseTitle}`
      : this.baseTitle;
  }
}
